import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { Link } from "react-router-dom"; // ✅ Needed for internal links
import '../App.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: 'packaging',
    message: ''
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false); // ✅ new state
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Block submission if checkbox not checked
    if (!acceptedTerms) {
      alert("⚠️ You must accept the Privacy Policy and Terms & Conditions before submitting.");
      return;
    }

    setLoading(true);

    const form = new FormData();
    form.append("name", formData.name);
    form.append("email", formData.email);
    form.append("company", formData.company);
    form.append("inquiryType", formData.inquiryType);
    form.append("message", formData.message);

    try {
      const response = await fetch("/send-mail.php", {
        method: "POST",
        body: form,
      });
      const result = await response.text();
      if (result === "success") {
        alert(`✅ Thank you, ${formData.name}! Your message has been sent. We've also sent a confirmation email to ${formData.email}.`);
        setFormData({
          name: '',
          email: '',
          company: '',
          inquiryType: 'packaging',
          message: ''
        });
        setAcceptedTerms(false); // ✅ reset checkbox
      } else {
        alert("❌ Oops! Something went wrong, please try again.");
      }
    } catch (error) {
      alert("❌ Error sending message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("contact-form-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: "Email Us",
      details: "info@magomeal.nl",
      subDetails: "We respond within 24 hours"
    },
    {
      icon: <Phone size={24} />,
      title: "Call Us",
      details: "+31 628 25 32 18",
      subDetails: "Mon-Fri, 9AM-6PM EST"
    },
    {
      icon: <MapPin size={24} />,
      title: "Post Address",
      details: "Postbus 103, 5240 AC, ROSMALEN",
    },
    {
      icon: <Clock size={24} />,
      title: "Business Hours",
      details: "Monday - Friday: 9AM - 6PM",
      subDetails: "Saturday: 10AM - 4PM"
    }
  ];

  const faqs = [
    {
      question: "Can I customize the packaging for my brand?",
      answer: "Absolutely! We offer custom printing for our edible packaging. Contact us to discuss your specific branding needs."
    },
    {
      question: "What are the cups made from?",
      answer: "The edible cup we offer is made of 7 natural ingredients only and mainly contains oat bran and wheat flour."
    },
    {
      question: "Are the cups suitable for a vegan diet?",
      answer: "Yes, absolutely! Our cups are made entirely from plant-based ingredients and coconut oil, so they’re 100% vegan-friendly."
    },
    {
      question: "How long can the cup hold liquids without softening?",
      answer: "Much longer than it takes to finish your drink! The cup stays sturdy during your beverage and remains leak-proof for many hours afterward, even up to a full day."
    },
    {
      question: "What's the minimum order quantity?",
      answer: "No minimum order quantity :)"
    },
    {
      question: "Do you offer bulk pricing for large orders?",
      answer: "Yes! We provide competitive bulk pricing and special rates for large volume orders. Contact our sales team at info@magomeal.nl for custom pricing information."
    }
  ];

  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="display-medium">Get In Touch</h1>
          <p className="body-large">
            Ready to join the edible packaging revolution? We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-info-grid">
            {contactInfo.map((info, index) => (
              <div key={index} className="contact-info-card network-card hover-lift">
                <div className="contact-icon" style={{ color: 'var(--brand-dark)' }}>
                  {info.icon}
                </div>
                <h3 className="heading-3">{info.title}</h3>
                <p className="body-medium" style={{ fontWeight: '600' }}>{info.details}</p>
                <p className="body-small">{info.subDetails}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form-section" className="contact-form-section">
        <div className="container">
          <div className="contact-form-content">
            <div className="form-header">
              <h2 className="heading-1">Send Us a Message</h2>
              <p className="body-large">
                If you're interested in our edible packaging solutions, we're here to help answer your questions.
              </p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company Name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="inquiryType" className="form-label">Inquiry Type *</label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="packaging">Edible Products Inquiry</option>
                    <option value="bulk">Bulk Order Inquiry</option>
                    <option value="general">General Question to MagoMeal</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className="form-textarea"
                  placeholder="Tell us about your project or ask us anything..."
                  required
                ></textarea>
              </div>

{/* ✅ Terms & Privacy Checkbox - Responsive & mobile friendly */}
<div
  className="form-group"
  style={{
    marginTop: "20px",
    marginBottom: "25px",
    padding: "14px",
    background: "#f8f8f8",
    borderRadius: "12px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  }}
>
<label
  style={{
    fontSize: "1rem",
    lineHeight: "1.8",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",         // ✅ allows spacing on larger screens
    gap: "6px"                // ✅ space between words
  }}
>
  <input
    type="checkbox"
    checked={acceptedTerms}
    onChange={(e) => setAcceptedTerms(e.target.checked)}
    required
    style={{
      marginRight: "12px",
      width: "20px",
      height: "20px",
      cursor: "pointer"
    }}
  />
  <span>I agree to the</span>
  <Link
    to="/privacy-policy"
    target="_blank"
    className="cookie-link"
    style={{ fontWeight: "600" }}
  >
    Privacy Policy
  </Link>
  <span>and</span>
  <Link
    to="/terms-conditions"
    target="_blank"
    className="cookie-link"
    style={{ fontWeight: "600" }}
  >
    Terms & Conditions
  </Link>
</label>

</div>



              <button type="submit" className="btn-primary form-submit" disabled={loading}>
                <Send size={20} />
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
